import axios from "axios";
import { BASE_API_URL } from "../config";
import { ONBOARDING_VERSION } from "../onboardingVersion";
import { saveSession } from "./session";
import { ROLES, roleForUserType } from "./AuthShell";
import { joinPath, pendingJoin } from "./pendingJoin";

export const INSTRUCTOR_HOME = "/classroom/teacher/dashboard";
// Where a student lands: their classrooms. The subjects page they chose at
// onboarding stays a click away under Home.
export const STUDENT_HOME = "/classrooms";

/**
 * Sign in with Google. Only the sign-up page passes `signUpAs`: that is the
 * one place an account may be created, because it's where the terms are
 * agreed. Without it a Google address with no account is refused, and
 * isNoGoogleAccount(error) is true. For an existing account the API ignores
 * `signUpAs`, and the type check in completeLogin decides whether they get in.
 */
export async function signInWithGoogle(code, signUpAs) {
  const { data } = await axios.post(`${BASE_API_URL}/api/v1/auth/sign-in/google`, {
    auth_code: code,
    user_type: signUpAs ? ROLES[signUpAs].userType : undefined,
  });
  return data;
}

export const isNoGoogleAccount = (error) => error?.response?.status === 404;

/**
 * The step after the credentials check out, shared by the login and sign-up
 * pages: make sure the account is the kind they chose, keep the session, and
 * send them on. Resolves to `{ wrongRole }` when the account is the other
 * kind — nothing is kept in that case — and throws if the API can't be read.
 */
export async function completeLogin({ session, role, navigate }) {
  const { access_token, token_type } = session;

  // Retrieve user details using the new token before keeping it, so an
  // account of the wrong kind never ends up signed in.
  const userResponse = await axios.get(`${BASE_API_URL}/api/v1/users/own`, {
    headers: {
      Authorization: `${token_type} ${access_token}`,
    },
  });

  const userDetails = userResponse.data;
  const userType = userDetails.user_type || "STUDENT";
  if (userType !== ROLES[role].userType) {
    return { wrongRole: roleForUserType(userType) };
  }

  saveSession(session);
  localStorage.setItem("userType", userType);
  localStorage.setItem("userGuid", userDetails.guid);
  localStorage.setItem("subscriptionPlanGuid", userDetails.active_subscription_plan_guid);

  const firstLetter = (userDetails.first_name || "").charAt(0).toUpperCase();
  localStorage.setItem("firstLetter", firstLetter);

  // They came from a class invite: back to it, to finish joining. That page
  // also explains if this account can't use it.
  const join = pendingJoin();
  if (join) {
    navigate(joinPath(join.token));
    return {};
  }

  if (role === "instructor") {
    navigate(INSTRUCTOR_HOME);
    return {};
  }

  // Arrived from the free practice paper email. Finish that journey before
  // the usual routing — including ahead of onboarding, because sitting the
  // paper is what they clicked for. FreePaperClaim clears the flag.
  if (sessionStorage.getItem("freePaperSubject")) {
    navigate("/free-paper");
    return {};
  }

  // Check if there's a plan in sessionStorage (for payment flow)
  const selectedPlan = sessionStorage.getItem("selectedPlan");

  if (selectedPlan) {
    // Proceed with payment by calling the checkout session API
    const subscriptionPlanGuid = userDetails.active_subscription_plan_guid;
    const stripePriceId = "price_1QkqC7C7LkUNUnN8V3nYcQd8";
    const renewalType = "MONTHLY";

    const checkoutResponse = await axios.post(
      `${BASE_API_URL}/api/v1/user-subscription-plans/create-checkout-session`,
      {
        subscription_plan_guid: subscriptionPlanGuid,
        stripe_price_id: stripePriceId,
        renewal_type: renewalType,
      },
      {
        headers: {
          Authorization: `${token_type} ${access_token}`,
        },
      }
    );

    const { checkout_url } = checkoutResponse.data;

    // Redirect to the Stripe checkout URL
    window.location.href = checkout_url;
    return {};
  }

  // Versioned onboarding gate
  const completedVersion = localStorage.getItem("hasCompletedOnboardingVersion");
  navigate(completedVersion !== ONBOARDING_VERSION ? "/onboarding" : STUDENT_HOME);
  return {};
}
